import React, { useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import MaxAndMediumScreen from '@/components/Device/MaxAndMediumScreen';
import Mobile from '@/components/Device/Mobile';
import ArrowLeft from '@/components/Icon/ArrowLeft';
import ArrowRight from '@/components/Icon/ArrowRight';

import QaItemCard from '../QaItemCard';

interface FaqItem {
  question: string;
  answer: string;
  key: string;
}

export interface QaCardProps {
  imageUrl: string;
  imagePosition?: 'left' | 'right';
  faqItems: FaqItem[];
  title?: string;
}

const QaCard: React.FC<QaCardProps> = ({ imageUrl, imagePosition = 'right', faqItems, title }) => {
  const { t } = useTranslation('common');
  const [currentPage, setCurrentPage] = useState(1);
  const [openIndex, setOpenIndex] = useState<string>('');

  const itemsPerPage = 4;
  const totalPages = Math.ceil(faqItems.length / itemsPerPage);

  const getCurrentPageItems = () => {
    const start = (currentPage - 1) * itemsPerPage;
    return faqItems.slice(start, start + itemsPerPage);
  };

  const renderContent = () => (
    <div className="flex-1">
      <MaxAndMediumScreen>
        <div className="flex justify-between">
          {title && <h2 className="mb-5 text-[48px] font-black text-wk-words-1 xl:mb-8">{title}</h2>}
          {totalPages > 1 && (
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="disabled:opacity-50"
              >
                <ArrowLeft />
              </button>
              <span className="text-[14px] font-medium text-wk-words-2 xl:text-[16px]">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="disabled:opacity-50"
              >
                <ArrowRight />
              </button>
            </div>
          )}
        </div>
        <div className="space-y-5">
          {getCurrentPageItems().map(({ key, answer, question }) => (
            <QaItemCard
              key={key}
              answer={answer}
              question={question}
              open={openIndex === key}
              onClick={() => setOpenIndex(openIndex === key ? '' : key)}
            />
          ))}
        </div>
      </MaxAndMediumScreen>
      <Mobile>
        {title && <h2 className="mb-6 text-center text-[28px] font-black text-wk-words-1">{title}</h2>}
        <div className="space-y-3">
          {faqItems.slice(0, currentPage * itemsPerPage).map(({ key, answer, question }) => (
            <QaItemCard
              key={key}
              answer={answer}
              question={question}
              open={openIndex === key}
              onClick={() => setOpenIndex(openIndex === key ? '' : key)}
            />
          ))}
          {currentPage < totalPages && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => {
                  setCurrentPage((prev) => prev + 1);
                }}
                className="flex items-center rounded-full border border-wk-words-1 px-6 py-2 text-wk-words-1"
              >
                {t('加载更多')}
              </button>
            </div>
          )}
        </div>
      </Mobile>
    </div>
  );

  return (
    <div className="flex items-start space-x-15 xl:space-x-20">
      <Mobile>{renderContent()}</Mobile>
      <MaxAndMediumScreen>
        {imagePosition === 'left' && (
          <Image src={imageUrl} alt="" width={317} height={422} className="object-cover xl:w-[451px]" />
        )}
        {renderContent()}
        {imagePosition === 'right' && (
          <Image src={imageUrl} width={317} height={422} alt="" className="object-cover xl:w-[451px]" />
        )}
      </MaxAndMediumScreen>
    </div>
  );
};

export default QaCard;
